import React from 'react';
import { PropTypes } from 'prop-types';
import styled, { keyframes } from 'styled-components';

import {
    Details as DefaultDetails,
    Content as DefaultContent
} from '../../containers/DefaultStyles/Shared';
import DefaultArrow from '../Svg/ThinArrow';

import { isPortrait } from '../../utils/size';
import { getImageUrl } from '../../utils/imageLoading';

const scaleFont = (start, end) => keyframes`
    from { 
        font-size: ${start}px;
    }
    to { 
        font-size: ${end}px;
    }
`;

const fadeIn = keyframes`
    from { 
        opacity: 0;
    }
    to { 
        opacity: 1;
    }
`;

const repositionGraph = (start, end) => keyframes`
    from { 
        flex: ${start};
    }
    to { 
        flex: ${end};
    }
`;

export const Details = styled(DefaultDetails)`
    position: relative;
    display: flex;
    width: 95%;
    margin: 0 auto;
    flex: auto;
    flex: 1;
    margin-bottom: ${props => (props.hasNextAction ? (isPortrait() ? 30 : 0) : 0)}px;
    ${props =>
        props.pollAnswered ? `justify-content: flex-start;` : `margin-top: 6px;`};
`;

export const Content = styled(DefaultContent)`
    height: 100%;
`;

export const GraphContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex: 0.65;
    ${props =>
        props.pollAnswered &&
        `animation: ${repositionGraph(0.5, props.maxSize)} 500ms forwards;`};
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: 5px;
`;

export const SliderSegmentContainer = styled.div`
    position: absolute;
    padding: 0 2px;
    opacity: 0.25;
    top: 7px;
    display: ${props => (props.pollAnswered ? `flex` : `none`)};
    flex-direction: row;
    width: 100%;

    > div {
        border-left: 1px solid black;
        border-right: 1px solid black;
        border-radius: 0.5px;
        &:nth-child(2) {
            border-left: none;
        }
    }
`;

export const SliderSegment = styled.div`
    height: 10px;
    width: ${props => props.width}%;
`;

export const Feedback = styled.h3`
    font-size: 14px;
    line-height: 17px;
    color: #444444;
    font-weight: bold;
    margin-top: 10px;
    opacity: 0;
    ${props => props.pollAnswered && `animation: ${fadeIn} 500ms 1.75s forwards;`};
`;

export const SliderContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    margin-top: -5px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

export const Slider = styled.input`
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
    width: 100%;
    height: 5px;
    border-radius: 2.5px;
    background: #c3c3c3;
    outline: none;
    opacity: 0.5;
    cursor: pointer;

    &:focus,
    &:focus-within,
    &:hover,
    &:active {
        outline: none;
    }
    &::-webkit-slider-thumb {
        visibility: hidden;
        -webkit-appearance: none;
        appearance: none;
        width: ${props => (props.pollAnswered ? 26 : 40)}px;
        height: ${props => (props.pollAnswered ? 26 : 40)}px;
        border-radius: ${props => (props.pollAnswered ? 26 : 40)}px;
        cursor: pointer;
    }
`;

export const SliderThumb = styled.div`
    position: absolute;
    pointer-events: none;
    left: ${props => props.position}%;
    transform: ${props => `translateX(-${props.pollAnswered ? 13 : 20}px)`};
    ${props =>
        !!props.customRadiatingColor &&
        `   img { background-color: ${props.customRadiatingColor} };
            path { stroke: ${props.customRadiatingColor} !important; }`};
    svg:nth-child(2) {
        position: absolute;
        width: 110px !important;
        height: 110px !important;
        top: -35px;
        left: -35px;
    }
    ${props =>
        props.pollAnswered
            ? ` width: 26px;
                height: 26px;
                border-radius: 26px;
                svg:nth-child(2) {
                    visibility: hidden;
                }`
            : ` height: 40px;
                width: 40px;
                border-radius: 40px;
                `}
`;

export const SentimentLabelContainer = styled.div`
    width: 94%;
    margin: 0 auto;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
`;

export const SentimentLabel = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    > div {
        width: 50%;
    }

    &:nth-child(1) {
        h4 {
            width: 80%;
            text-align: left;
            justify-content: flex-start;
            padding: ${props =>
                props.pollAnswered && props.hasNextAction ? `0` : `5px 0`};
            padding-left: ${() => (isPortrait() ? 3 : 10)}px;
            margin-right: 20%;
        }
    }
    &:nth-child(2) {
        h4 {
            width: 80%;
            text-align: right;
            justify-content: flex-end;
            padding: ${props =>
                props.pollAnswered && props.hasNextAction ? `0` : `5px 0`};
            padding-right: ${() => (isPortrait() ? 3 : 10)}px;
            margin-left: 20%;
        }
    }

    h5 {
        color: #ff245e;
        font-size: 14px;
        font-weight: 600;
        margin: 10px 0 0;
        text-align: center;
        opacity: 0;
        ${props => props.pollAnswered && `animation: ${fadeIn} 500ms 1000ms forwards;`};
    }
    h4 {
        display: flex;
        width: 100%;
        color: #333333;
        font-size: 14px;
        font-weight: 600;
        text-align: center;
        padding: 0;
        margin: ${props => (props.pollAnswered ? `0` : `10px 0 0`)};

        .emoji {
            font-size: 42px;
            ${props =>
                props.pollAnswered &&
                `animation: ${scaleFont(42, 14)} 1000ms 200ms forwards;`};
        }
    }
`;

const BoltIcon = styled.svg`
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: ${props => (props.pollAnswered ? 26 : 40)}px;
    height: ${props => (props.pollAnswered ? 26 : 40)}px;
    border-radius: ${props => (props.pollAnswered ? 26 : 40)}px;
`;

const CustomThumbIcon = styled.img`
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: ${props => (props.pollAnswered ? 26 : 40)}px;
    height: ${props => (props.pollAnswered ? 26 : 40)}px;
    border-radius: ${props => (props.pollAnswered ? 26 : 40)}px;
`;

export const SliderLogo = props => {
    if (!!props.customLogoId) {
        return (
            <CustomThumbIcon
                isPaused={props.isPaused}
                pollAnswered={props.pollAnswered}
                src={getImageUrl(props.customLogoId)}
            />
        );
    } else {
        return (
            <BoltIcon
                isPaused={props.isPaused}
                pollAnswered={props.pollAnswered}
                cursor="pointer"
                viewBox="0 0 592.48 592.48"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g>
                    <path
                        fill="#ffffff"
                        d="M345.46,249.94s51.78-174.75,58.21-195c6.33-20,23.72-24.58,39.14-16.17-19.47-11.1-23.34-11.67-23.34-11.67s-13.91.29-31.07,21.13C376.62,62.53,124.62,342.55,124.62,342.55H247.08s-51.77,174.75-58.19,195-24.17,24.71-39.74,15.85c-.05,0,0,0,0,0s12.38,7.31,21.5,11.54a15.49,15.49,0,0,1,3.51.5A35.72,35.72,0,0,0,196,553c12.09-14.77,271.84-303,271.84-303Z"
                    />
                    <path fill="#ffffff" d="M443.54,39.16l-.73-.4,1.88,1.07Z" />
                    <path
                        fill="#fc2a61"
                        d="M215.8,581.43a294.81,294.81,0,0,0,80.43,11.05c163.62,0,296.25-132.63,296.25-296.23A296.11,296.11,0,0,0,443.54,39.16c-15.6-9-33.44-4.49-39.87,15.77s-58.21,195-58.21,195h122.4S208.11,538.22,196,553c-11,13.46-25.48,12-25.48,12s27.69,11.79,45.26,16.5"
                    />
                    <path
                        fill="#fc2a61"
                        d="M.06,296.25c0,110,59.94,206,148.94,257.08,15.6,9,33.45,4.49,39.89-15.76s58.19-195,58.19-195H124.62s252-280,263.78-294.33c17.16-20.84,31.07-21.13,31.07-21.13a292,292,0,0,0-42.78-16A298.5,298.5,0,0,0,296.25,0C132.63,0,0,132.63,0,296.25H.06"
                    />
                </g>
            </BoltIcon>
        );
    }
};

SliderLogo.propTypes = {
    isPaused: PropTypes.bool.isRequired,
    pollAnswered: PropTypes.bool.isRequired,
    customLogoId: PropTypes.string
};

const ArrowContainer = styled.div`
    position: absolute;
    display: flex;
    justify-content: space-around;
    display: flex;
    position: absolute;
    pointer-events: none;
    display: flex;
    width: 100px;
    left: ${props => props.sliderPosition}%;
    transform: translateX(-50px);
`;

const Arrow = styled(DefaultArrow)`
    z-index: 2;
    position: relative;
    pointer-events: none;
    transition: opacity 500ms ease;
    ${props =>
        props.hover || isPortrait()
            ? ` height: ${props.height}px;
                width: ${props.width}px;
                opacity: 1;`
            : ` height: ${props.height * 1.25}px;
                width: ${props.width * 1.25}px;
                opacity: 0;`};
    ${props =>
        props.right &&
        ` transform: rotate(180deg);
          margin-right: ${props.pollAnswered ? 15 : 55}px;`};
`;

export const Arrows = props => (
    <ArrowContainer
        pollAnswered={props.pollAnswered}
        sliderPosition={props.sliderPosition}
    >
        <Arrow
            color={'#999'}
            height={props.pollAnswered ? 16 : 32}
            hover={props.hover}
            isPaused={props.isPaused}
            right
            pollAnswered={props.pollAnswered}
            width={props.pollAnswered ? 16 : 32}
        />
        <Arrow
            color={'#999'}
            height={props.pollAnswered ? 16 : 32}
            hover={props.hover}
            isPaused={props.isPaused}
            left
            pollAnswered={props.pollAnswered}
            width={props.pollAnswered ? 16 : 32}
        />
    </ArrowContainer>
);

Arrows.propTypes = {
    hover: PropTypes.bool,
    isPaused: PropTypes.bool,
    pollAnswered: PropTypes.bool,
    sliderPosition: PropTypes.number
};
